/**
 * ContributorCertificate — Official Polyglot Translator certificate page.
 *
 * Shows the contributor's certificate with live stats: enrolled date,
 * training date, assigned languages, attestation counts, corrections.
 * Designed to be shareable (screenshot/print-friendly).
 *
 * Route: /polyglot/certificate
 * API: GET /@polyglot/contributors/me/certificate
 *
 * DR-2026-067, WI-TRN-002 §5
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Icon,
  Loader,
  Message,
  Label,
  Button,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, fetchApi } from '../../api';

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

export default function ContributorCertificate() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchApi(`${getApiPath()}/@polyglot/contributors/me/certificate`)
      .then((json) => { setData(json); setLoading(false); })
      .catch((err) => { setError(err.message); setLoading(false); });
  }, []);

  if (loading) {
    return (
      <Container style={{ paddingTop: '3em', maxWidth: '800px' }}>
        <Loader active inline="centered" content="Loading certificate..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ paddingTop: '3em', maxWidth: '800px' }}>
        <Message warning>
          <Message.Header>Certificate not available</Message.Header>
          <p>{error}</p>
          <p><Link to="/polyglot/training">Complete onboarding training</Link> to receive your certificate.</p>
        </Message>
      </Container>
    );
  }

  const { certificate_id, fullname, enrolled_at, training_completed_at, languages, qualifications, stats } = data;

  return (
    <Container style={{ paddingTop: '2em', paddingBottom: '3em', maxWidth: '800px' }}>
      <Helmet title={`Certificate — ${fullname}`} />

      {/* Certificate card */}
      <div
        id="certificate"
        style={{
          background: 'linear-gradient(135deg, #f8fbff 0%, #f0f7f0 100%)',
          border: '3px solid #2185d0',
          borderRadius: '12px',
          padding: '3em 2.5em',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative corner accents */}
        <div style={{ position: 'absolute', top: '12px', left: '12px', width: '40px', height: '40px', borderTop: '3px solid #21ba45', borderLeft: '3px solid #21ba45' }} />
        <div style={{ position: 'absolute', top: '12px', right: '12px', width: '40px', height: '40px', borderTop: '3px solid #21ba45', borderRight: '3px solid #21ba45' }} />
        <div style={{ position: 'absolute', bottom: '12px', left: '12px', width: '40px', height: '40px', borderBottom: '3px solid #21ba45', borderLeft: '3px solid #21ba45' }} />
        <div style={{ position: 'absolute', bottom: '12px', right: '12px', width: '40px', height: '40px', borderBottom: '3px solid #21ba45', borderRight: '3px solid #21ba45' }} />

        <div style={{ fontSize: '0.9em', textTransform: 'uppercase', letterSpacing: '0.3em', color: '#2185d0', marginBottom: '0.5em' }}>
          Polyglot Translation Project
        </div>

        <Header as="h1" style={{ fontSize: '2.2em', margin: '0.3em 0', color: '#1a1a1a' }}>
          Official Polyglot Translator
        </Header>

        <div style={{ fontSize: '0.85em', color: '#888', marginBottom: '2em' }}>
          Certificate of Completion
        </div>

        <div style={{ fontSize: '2em', fontWeight: '600', color: '#333', margin: '0.5em 0' }}>
          {fullname}
        </div>

        <div style={{ margin: '1.5em 0' }}>
          <div style={{ fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#888', marginBottom: '0.5em' }}>
            Certified for
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5em', flexWrap: 'wrap' }}>
            {languages.map((lang) => (
              <Label key={lang.code} color="blue" size="large">
                {lang.name}
                {lang.native_name && lang.native_name !== lang.name && (
                  <Label.Detail>{lang.native_name}</Label.Detail>
                )}
              </Label>
            ))}
          </div>
        </div>

        {/* Qualifications */}
        {qualifications && qualifications.length > 0 && (
          <div style={{ margin: '1.5em auto', maxWidth: '500px', textAlign: 'left' }}>
            <div style={{ fontSize: '0.75em', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#888', marginBottom: '0.5em', textAlign: 'center' }}>
              Declared Qualifications
            </div>
            {qualifications.map((q) => (
              <div key={q.language_code} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3em 0', borderBottom: '1px solid #eee', fontSize: '0.95em' }}>
                <span>
                  <strong>{q.language_name}</strong>
                  {q.is_native && <Label size="mini" color="green" style={{ marginLeft: '0.5em' }}>Native</Label>}
                </span>
                <span style={{ color: '#666' }}>
                  {q.proficiency_level && !q.is_native && q.proficiency_level}
                  {q.years_experience && ` · ${q.years_experience} yr${q.years_experience > 1 ? 's' : ''}`}
                </span>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'center', gap: '3em', margin: '2em 0', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.75em', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#888' }}>Enrolled</div>
            <div style={{ fontSize: '1.1em', fontWeight: '500' }}>{formatDate(enrolled_at)}</div>
          </div>
          <div>
            <div style={{ fontSize: '0.75em', textTransform: 'uppercase', letterSpacing: '0.1em', color: '#888' }}>Training Completed</div>
            <div style={{ fontSize: '1.1em', fontWeight: '500' }}>{formatDate(training_completed_at)}</div>
          </div>
        </div>

        <div style={{ fontSize: '0.8em', color: '#aaa', marginTop: '2em' }}>
          {certificate_id} &middot; Ljusa Tider AB
        </div>
      </div>

      {/* Live stats section — below the certificate */}
      <Segment style={{ marginTop: '2em' }}>
        <Header as="h3">
          <Icon name="chart bar" />
          <Header.Content>
            Your Contribution
            <Header.Subheader>Live statistics from the translation database</Header.Subheader>
          </Header.Content>
        </Header>

        <div style={{ display: 'flex', gap: '2em', justifyContent: 'center', flexWrap: 'wrap', margin: '1.5em 0' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5em', fontWeight: '700', color: '#2185d0' }}>{stats.total_attestations}</div>
            <div style={{ fontSize: '0.85em', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Reviews</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5em', fontWeight: '700', color: '#21ba45' }}>{stats.looks_good}</div>
            <div style={{ fontSize: '0.85em', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Approved</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5em', fontWeight: '700', color: '#f2711c' }}>{stats.needs_attention}</div>
            <div style={{ fontSize: '0.85em', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Flagged</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5em', fontWeight: '700', color: '#6435c9' }}>{stats.total_contributions}</div>
            <div style={{ fontSize: '0.85em', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contributions</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.5em', fontWeight: '700', color: '#00b5ad' }}>{stats.languages_reviewed}</div>
            <div style={{ fontSize: '0.85em', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Languages</div>
          </div>
        </div>
      </Segment>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '1em', justifyContent: 'center', marginTop: '1.5em' }}>
        <Button primary as={Link} to="/polyglot/dashboard">
          <Icon name="arrow left" /> Dashboard
        </Button>
        <Button basic onClick={() => window.print()}>
          <Icon name="print" /> Print Certificate
        </Button>
      </div>
    </Container>
  );
}
